import { useState } from "react";
import { ChevronDown, ChevronRight, Plus, Trash2, RefreshCcw } from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  CONDITION_OPTIONS, SCOPE_OPTIONS, FINISH_OPTIONS, ROOM_TYPES, FINISH_MULT,
} from "@/lib/roomTemplates";
import { roomTotals, workItemTotal } from "@/lib/calculations";
import { useFormatCurrency } from "@/lib/CurrencyContext";
import { getRoomItemHint, FINISH_HINT } from "@/lib/calcHints";
import CalcHelp from "@/components/CalcHelp";
import AddItemDialog from "@/components/AddItemDialog";

const KIND_OPTIONS = [
  { value: "material", label: "Material" },
  { value: "labour", label: "Labour" },
  { value: "mixed", label: "Mixed" },
];

const UNIT_OPTIONS = [
  "sqm", "lm", "item", "set", "room", "days", "hours",
];

export default function RoomCard({ room, onChange, onDelete, onRegenerate, onAddRoom, budgetPct, costLibrary = null, currency = "USD" }) {
  const [open, setOpen] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const totals = roomTotals(room);
  const fmt = useFormatCurrency();

  const updateItem = (id, patch) => {
    onChange({
      work_items: (room.work_items || []).map((i) =>
        i.id === id ? { ...i, ...patch } : i
      ),
    });
  };

  const deleteItem = (id) => {
    onChange({
      work_items: (room.work_items || []).filter((i) => i.id !== id),
    });
  };

  const addItemFromDialog = (item) => {
    onChange({
      work_items: [...(room.work_items || []), item],
    });
  };

  return (
    <div className="fc-card p-0 overflow-hidden" data-testid={`room-card-${room.id}`}>
      {/* Header */}
      <div className="p-4 sm:p-5 border-b border-sage-200 bg-sage-50">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <button
              onClick={() => setOpen((v) => !v)}
              className="text-[#A8A29E] hover:text-[#1C1C1C] transition-colors"
              aria-label="Toggle room"
              data-testid={`toggle-room-${room.id}`}
            >
              {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            </button>
            <input
              data-testid={`room-name-input-${room.id}`}
              value={room.name}
              onChange={(e) => onChange({ name: e.target.value })}
              className="fc-heading bg-transparent border-0 outline-none text-[#1C1C1C] text-[15px] font-medium w-full min-w-0 focus:outline-none"
              placeholder="Room name"
            />
          </div>
          <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap justify-end">
            <button
              data-testid={`regenerate-items-${room.id}`}
              onClick={onRegenerate}
              className="text-xs text-[#78716C] hover:text-[#1C1C1C] flex items-center gap-1 border border-black/[0.08] hover:border-black/[0.2] rounded-md px-2 py-1 transition-colors"
              title="Regenerate default items"
            >
              <RefreshCcw className="w-3 h-3" /> Regen
            </button>
            <button
              data-testid={`delete-room-${room.id}`}
              onClick={onDelete}
              className="text-[#A8A29E] hover:text-[#DC2626] transition-colors"
              aria-label="Delete room"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Attributes */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-4">
          <Field label="Type">
            <MiniSelect
              tid={`room-type-${room.id}`}
              value={room.room_type}
              onChange={(v) => onChange({ room_type: v })}
              options={ROOM_TYPES}
            />
          </Field>
          <Field label="Size (sqm)">
            <Input
              data-testid={`room-size-${room.id}`}
              type="number"
              value={room.size_sqm}
              onChange={(e) => onChange({ size_sqm: Number(e.target.value) || 0 })}
              className="fc-input h-9 rounded-md text-sm"
            />
          </Field>
          <Field label="Condition">
            <MiniSelect
              tid={`room-condition-${room.id}`}
              value={room.condition}
              onChange={(v) => onChange({ condition: v })}
              options={CONDITION_OPTIONS}
            />
          </Field>
          <Field label="Scope">
            <MiniSelect
              tid={`room-scope-${room.id}`}
              value={room.renovation_scope}
              onChange={(v) => onChange({ renovation_scope: v })}
              options={SCOPE_OPTIONS}
            />
          </Field>
          <Field label="Finish">
            <MiniSelect
              tid={`room-finish-${room.id}`}
              value={room.finish_level}
              onChange={(v) => onChange({ finish_level: v })}
              options={FINISH_OPTIONS}
            />
          </Field>
        </div>

        {/* Totals bar */}
        <div className="grid grid-cols-3 gap-3 mt-5 pt-4 border-t border-black/[0.06]">
          <Stat
            label="Total"
            value={fmt(totals.total)}
            tid={`room-total-${room.id}`}
            strong
            sub={typeof budgetPct === "number" ? `${budgetPct.toFixed(1)}% of budget` : null}
            subTid={`room-budget-pct-${room.id}`}
            help={
              <CalcHelp
                testId={`room-total-help-${room.id}`}
                title="Room total"
                formula={`Labour ${fmt(totals.labour)} + Materials ${fmt(totals.material)} = ${fmt(totals.total)}`}
                lines={[
                  "Mixed-kind items (e.g. Tiling, Paint) are split 50/50 between labour and materials.",
                  FINISH_HINT[room.finish_level],
                ]}
              />
            }
          />
          <Stat label="Labour" value={fmt(totals.labour)} tid={`room-labour-${room.id}`} />
          <Stat label="Materials" value={fmt(totals.material)} tid={`room-material-${room.id}`} />
        </div>
      </div>

      {/* Work items */}
      {open && (
        <div className="p-4 sm:p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="fc-label">Upgrades</div>
            <button
              data-testid={`add-item-${room.id}`}
              onClick={() => setAddOpen(true)}
              className="text-xs text-[#78716C] hover:text-[#1C1C1C] flex items-center gap-1 border border-black/[0.08] hover:border-black/[0.2] rounded-md px-2 py-1 transition-colors"
            >
              <Plus className="w-3 h-3" /> Add Item
            </button>
          </div>

          {(room.work_items || []).length === 0 ? (
            <div className="text-xs text-[#A8A29E] py-6 text-center border border-dashed border-black/[0.06] rounded-md">
              No items yet. Click <span className="text-[#1C1C1C] font-medium">Add Item</span> to pick from your Cost Library or build a custom one.
            </div>
          ) : (
            <div className="overflow-x-auto fc-no-scrollbar -mx-4 sm:mx-0 px-4 sm:px-0">
              <table className="w-full min-w-[640px] text-sm">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-[0.15em] text-[#A8A29E]">
                    <th className="py-2 pr-2 font-normal">Name</th>
                    <th className="py-2 pr-2 font-normal w-24">Kind</th>
                    <th className="py-2 pr-2 font-normal w-20">Qty</th>
                    <th className="py-2 pr-2 font-normal w-20">Unit</th>
                    <th className="py-2 pr-2 font-normal w-28">Rate</th>
                    <th className="py-2 pr-2 font-normal w-28 text-right">Total</th>
                    <th className="py-2 pl-2 font-normal w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {room.work_items.map((it) => (
                    <tr key={it.id} className="border-t border-black/[0.04]" data-testid={`item-row-${it.id}`}>
                      <td className="py-1.5 pr-2">
                        <input
                          data-testid={`item-name-${it.id}`}
                          value={it.name}
                          onChange={(e) => updateItem(it.id, { name: e.target.value })}
                          className="bg-transparent border-0 outline-none text-sm text-[#1C1C1C] w-full focus:outline-none"
                        />
                      </td>
                      <td className="py-1.5 pr-2">
                        <MiniSelect
                          tid={`item-kind-${it.id}`}
                          value={it.kind}
                          onChange={(v) => updateItem(it.id, { kind: v })}
                          options={KIND_OPTIONS}
                          compact
                        />
                      </td>
                      <td className="py-1.5 pr-2">
                        <Input
                          data-testid={`item-qty-${it.id}`}
                          type="number"
                          value={it.quantity}
                          onChange={(e) => updateItem(it.id, { quantity: Number(e.target.value) || 0 })}
                          className="fc-input h-8 text-sm rounded-md px-2"
                        />
                      </td>
                      <td className="py-1.5 pr-2">
                        <select
                          data-testid={`item-unit-${it.id}`}
                          value={it.unit}
                          onChange={(e) => updateItem(it.id, { unit: e.target.value })}
                          className="fc-input h-8 text-sm rounded-md px-2 w-full"
                        >
                          {UNIT_OPTIONS.map((u) => <option key={u} value={u}>{u}</option>)}
                        </select>
                      </td>
                      <td className="py-1.5 pr-2">
                        <Input
                          data-testid={`item-rate-${it.id}`}
                          type="number"
                          value={it.rate}
                          onChange={(e) => updateItem(it.id, { rate: Number(e.target.value) || 0 })}
                          className="fc-input h-8 text-sm rounded-md px-2"
                        />
                      </td>
                      <td className="py-1.5 pr-2 text-right text-[#1C1C1C] fc-mono text-sm" data-testid={`item-total-${it.id}`}>
                        <span className="inline-flex items-center gap-1.5 justify-end">
                          <span>{fmt(workItemTotal(it))}</span>
                          {getRoomItemHint(room.room_type, it.name) && (
                            <CalcHelp
                              testId={`item-total-help-${it.id}`}
                              title={`${it.name} — ${ROOM_TYPES.find(r => r.value === room.room_type)?.label || room.room_type}`}
                              formula={`${it.quantity} ${it.unit} × ${fmt(it.rate)} = ${fmt(workItemTotal(it))}`}
                              lines={[
                                getRoomItemHint(room.room_type, it.name),
                                FINISH_HINT[room.finish_level],
                              ]}
                            />
                          )}
                        </span>
                      </td>
                      <td className="py-1.5 pl-2 text-right">
                        <button
                          data-testid={`delete-item-${it.id}`}
                          onClick={() => deleteItem(it.id)}
                          className="text-[#A8A29E] hover:text-[#DC2626] transition-colors"
                          aria-label="Delete item"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {onAddRoom && (
            <div className="pt-4 mt-4 border-t border-black/[0.06] flex justify-center">
              <button
                type="button"
                data-testid={`footer-add-room-${room.id}`}
                onClick={onAddRoom}
                className="text-sm text-white bg-[#1C1C1C] hover:bg-[#2A2A2E] flex items-center gap-1.5 rounded-md px-4 py-2 transition-colors"
              >
                <Plus className="w-4 h-4" /> Add New Room
              </button>
            </div>
          )}
        </div>
      )}

      <AddItemDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        room={room}
        costLibrary={costLibrary}
        currency={currency}
        finishMult={FINISH_MULT[room.finish_level] ?? 1}
        onAddItem={addItemFromDialog}
      />
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="min-w-0">
      <div className="fc-label mb-1">{label}</div>
      {children}
    </div>
  );
}

function Stat({ label, value, tid, strong = false, sub = null, subTid, help = null }) {
  return (
    <div>
      <div className="fc-label flex items-center justify-between gap-1">
        <span>{label}</span>
        {help}
      </div>
      <div className={`mt-0.5 ${strong ? "fc-heading text-[#1C1C1C] text-base" : "text-[#27272A] text-sm"}`} data-testid={tid}>
        {value}
      </div>
      {sub && (
        <div className="text-[11px] text-[#78716C] mt-0.5" data-testid={subTid}>{sub}</div>
      )}
    </div>
  );
}

function MiniSelect({ tid, value, onChange, options, compact = false }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger
        data-testid={tid}
        className={`fc-input rounded-md text-sm ${compact ? "h-8" : "h-9"}`}
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent className="bg-white border-black/[0.08] text-[#27272A]">
        {options.map((o) => (
          <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
